import Phaser from 'phaser';
import { GameScene } from '../GameScene';
import { TILE_SIZE, ITEM_RARITY_COLORS, ItemRarity } from '@shared/constants';
import { ClientMessageType, type GroundItemsSyncMessage } from '@shared/types/messages';
import { getItemTemplate } from '@shared/data/items';
import { formatItemName } from '@shared/utils/items';
import { UNIQUE_FLOOR_LOOP_SOUND } from '@shared/data/sounds';

/** Key del loop posicional del ítem único de id `itemId`. */
const uniqueLoopKey = (itemId: string): string => `unique_${itemId}`;

/** Key de la luz real (LightingSystem) del ítem único de id `itemId`. */
const uniqueLightKey = (itemId: string): string => `unique_light_${itemId}`;

/** Owner de los emitters de piso por `vfx.ground` (F5), para cleanup en masa por ítem. */
const groundFxOwner = (itemId: string): string => `grounditem_${itemId}`;

// Luz real del ítem único en el piso: cálida, pequeña y suave (sólo se percibe de noche/oscuridad).
const UNIQUE_LIGHT_RADIUS = 50; // ~50% más chica que el tamaño original
const UNIQUE_LIGHT_INTENSITY = 0.5;
const UNIQUE_LIGHT_COLOR = 0xfff2cc;

// Glow aditivo del ítem único: color cálido y período del latido (mismo que la luz real).
const UNIQUE_GLOW_COLOR = 0xffe9b0;
const HEARTBEAT_PERIOD_S = 3.6; // mismo período que LightingSystem.PULSE_PERIOD → en fase (mantener iguales)

// Glow de silueta (WebGL preFX): el brillo NACE del borde del gráfico del ítem (respeta su alpha),
// no un círculo detrás. `outerStrength` late con el heartbeat entre MIN (reposo) y MAX (golpe).
const UNIQUE_GLOW_OUTER_MIN = 1.6; // reposo: contorno tenue pero presente
const UNIQUE_GLOW_OUTER_MAX = 5.2; // pico del latido: el contorno "canta"
const UNIQUE_GLOW_DISTANCE = 10; // alcance del halo en px hacia afuera del borde
const UNIQUE_GLOW_PADDING = 14; // > distance para que el halo no se recorte en el borde del sprite
const UNIQUE_GLOW_QUALITY = 0.1; // pasos del sample (más alto = más suave y más caro)

/**
 * Onda de latido "lub-dub" en [0,1] (0 = reposo, 1 = pico), continua en los extremos del ciclo.
 * Misma forma que el pulso de la luz real (`LightingSystem.effectiveIntensity`) para que el glow
 * y la luz respiren con la misma intención rítmica. `t` = tiempo en ciclos (no normalizado).
 */
function heartbeatWave(t: number): number {
  const phase = t - Math.floor(t);
  const dist0 = Math.min(phase, 1 - phase); // distancia circular alrededor de 0 (1er golpe)
  const bump0 = Math.exp(-(dist0 * dist0) / (2 * 0.05 * 0.05));
  const d1 = phase - 0.16; // 2do golpe, apenas después
  const bump1 = 0.6 * Math.exp(-(d1 * d1) / (2 * 0.045 * 0.045));
  return Math.min(1, bump0 + bump1);
}

/**
 * Items en el piso (groundItems): aplica el sync del server creando/actualizando/removiendo los
 * sprites+labels+efectos (glow de rareza SET y de encantamiento), efectos visuales, request de
 * pickup y el reposicionamiento por frame. Extraído de GameScene (god-object). El estado
 * (groundItemObjects) vive en GameScene (host, lo toca el teardown de render) y se accede vía
 * `this.scene.*`.
 */
export class GroundItemsController {
  constructor(private scene: GameScene) {}

  public applyGroundItemsSync(message: GroundItemsSyncMessage): void {
    // Detectar si es la carga inicial (cuando groundItemObjects está vacío)

    const isInitialLoad = this.scene.groundItemObjects.size === 0;

    // Primero, hacer fade-out de los items que ya no están presentes

    const nextIds = new Set<string>(

      (message.items as GroundItemsSyncMessage['items']).map(

        (it: GroundItemsSyncMessage['items'][number]) => it.id

      )

    );

    this.scene.groundItemObjects.forEach((obj, id) => {
      if (!nextIds.has(id)) {
        // Cortar los efectos del ítem único (no-op si no era único): sonido en loop,
        // luz real y destello (motor de partículas, por owner). El glow aditivo hace
        // fade con el grupo y se destruye abajo.
        this.scene.audio.stopLoop(uniqueLoopKey(id));
        this.scene.lightingSystem?.removeLightSource(uniqueLightKey(id));
        this.scene.particleManager?.stopAllByOwner(groundFxOwner(id));
        const targets: Phaser.GameObjects.GameObject[] = [obj.sprite, obj.label];
        const placeholder = obj.sprite.getData('placeholder');
        if (placeholder) {
          targets.push(placeholder);
        }
        // Incluir efectos en el fade out si existen
        if (obj.effect) {
          targets.push(obj.effect);
        }
        if (obj.enchantEffect) {
          targets.push(obj.enchantEffect);
        }
        if (obj.uniqueEffect) {
          targets.push(obj.uniqueEffect);
        }
        this.scene.tweens.add({
          targets,
          alpha: 0,
          duration: 200,
          onComplete: () => {
            obj.sprite.getData('hoverZone')?.destroy();
            obj.sprite.destroy();
            obj.label.destroy();
            obj.effect?.destroy();
            obj.enchantEffect?.destroy();
            obj.uniqueEffect?.destroy();
            if (placeholder) {
              placeholder.destroy();
            }
            this.scene.groundItemObjects.delete(id);
          },
        });
      }
    });

    // Actualizar / crear los items actuales
    // AO-like: Usar coordenadas de mundo directamente (la cámara se mueve sobre ellos)
    message.items.forEach((item: GroundItemsSyncMessage['items'][number]) => {
      const worldX = item.x * TILE_SIZE + TILE_SIZE / 2;
      const worldY = item.y * TILE_SIZE + TILE_SIZE / 2;

      // Ítem ÚNICO en el piso → LOOP posicional + LUZ real con heartbeat lento (idempotentes:
      // startLoop/addLightSource ignoran si ya existen). El glow aditivo y el destello (partículas)
      // se crean/reposicionan por ítem en las ramas de abajo. Se corta todo al removerse (arriba).
      // Ver docs/SOUND_AND_MUSIC.md y docs/PARTICLES.md.
      const isUnique = getItemTemplate(item.itemId)?.unique === true;
      if (isUnique) {
        this.scene.audio.startLoop(
          UNIQUE_FLOOR_LOOP_SOUND,
          { tileX: item.x, tileY: item.y },
          uniqueLoopKey(item.id)
        );
        if (this.scene.lightingSystem) {
          if (!this.scene.lightingSystem.hasLightSource(uniqueLightKey(item.id))) {
            this.scene.lightingSystem.addLightSource(uniqueLightKey(item.id), {
              x: worldX,
              y: worldY,
              radius: UNIQUE_LIGHT_RADIUS,
              intensity: UNIQUE_LIGHT_INTENSITY,
              color: UNIQUE_LIGHT_COLOR,
              pulse: true,
            });
          } else {
            this.scene.lightingSystem.updateLightPosition(uniqueLightKey(item.id), worldX, worldY);
          }
        }
      }

      // Calcular depth basado en la posición Y del item
      const baseDepthForEntities = this.scene.getEntityBaseDepthFromTileY(item.y);
      // Los items en el suelo deben verse por encima de las entidades en la misma Y o por debajo
      const itemRectDepth = baseDepthForEntities - 0.5;
      const itemLabelDepth = itemRectDepth + 0.01; // Label ligeramente por encima del sprite

      const existing = this.scene.groundItemObjects.get(item.id);

      if (existing) {
        // Actualizar posición / texto (en caso de que cambie cantidad)
        const itemTemplate = getItemTemplate(item.itemId);
        const iconKey = itemTemplate && this.scene.textures.exists(`item_icon_${itemTemplate.icon}`)
          ? `item_icon_${itemTemplate.icon}`
          : null;

        if (iconKey) {
          existing.sprite.setTexture(iconKey);
          existing.sprite.setVisible(true);
          const placeholder = existing.sprite.getData('placeholder');
          if (placeholder) {
            placeholder.setVisible(false);
          }
        }

        // Actualizar nombre del item (no el ID)
        const itemName = formatItemName(item.itemId, item.enchantId, item.rolledRarity, item.rolledAffixes);
        existing.label.setText(itemName);

        // AO-like: Actualizar posiciones usando coordenadas de mundo (la cámara se mueve sobre ellos)
        existing.sprite
          .setPosition(worldX, worldY)
          .setDepth(itemRectDepth)
          .setAlpha(1);
        
        // AO-like: Guardar coordenadas de mundo para actualización en cada frame
        existing.sprite.setData('worldX', worldX);
        existing.sprite.setData('worldY', worldY);

        existing.label
          .setPosition(worldX, worldY + 20)
          .setDepth(itemLabelDepth);

        // Actualizar o crear efecto visual según rolledRarity de la instancia.
        const rarity = item.rolledRarity as ItemRarity;
        if (rarity === ItemRarity.SET && ITEM_RARITY_COLORS[rarity]) {
          if (existing.effect) {
            // Actualizar posición del efecto existente
            existing.effect.setPosition(worldX, worldY);
            existing.effect.setDepth(itemRectDepth - 0.01);
          } else {
            // Crear nuevo efecto
            existing.effect = this.createGroundItemEffect(worldX, worldY, ITEM_RARITY_COLORS[rarity], itemRectDepth);
          }
        } else if (existing.effect) {
          // Eliminar efecto si el item ya no es de set
          existing.effect.destroy();
          existing.effect = undefined;
        }

        // Actualizar o crear efecto visual de encantamiento
        if (item.enchantId) {
          if (existing.enchantEffect) {
            // Actualizar posición del efecto existente
            existing.enchantEffect.setPosition(worldX, worldY);
            existing.enchantEffect.setDepth(itemRectDepth - 0.005); // Sobre el efecto de rareza
          } else {
            // Crear nuevo efecto de encantamiento
            existing.enchantEffect = this.createGroundEnchantmentEffect(worldX, worldY, itemRectDepth);
          }
        } else if (existing.enchantEffect) {
          // Eliminar efecto si el item ya no tiene encantamiento
          existing.enchantEffect.destroy();
          existing.enchantEffect = undefined;
        }

        // Glow de silueta del ítem único (o glow-círculo en Canvas). El glow FX se cuelga del
        // sprite, así que no hay que reposicionarlo; sólo garantizar que exista (p.ej. tras
        // hot-reload). El fallback-círculo sí se reposiciona.
        if (isUnique) {
          if (!existing.uniqueGlow && !existing.uniqueEffect) {
            existing.uniqueGlow = this.attachUniqueGlow(existing.sprite);
            if (!existing.uniqueGlow) {
              existing.uniqueEffect = this.createUniqueGroundEffect(worldX, worldY, itemRectDepth);
            }
          }
          if (existing.uniqueEffect) {
            existing.uniqueEffect.setPosition(worldX, worldY);
            existing.uniqueEffect.setDepth(itemRectDepth - 0.008);
          }
        }

        // Actualizar información del item
        existing.itemId = item.itemId;
        existing.quantity = item.quantity;
        existing.id = item.id;
      } else {
        // Obtener el icono del item
        const itemTemplate = getItemTemplate(item.itemId);
        const iconKey = itemTemplate && this.scene.textures.exists(`item_icon_${itemTemplate.icon}`)
          ? `item_icon_${itemTemplate.icon}`
          : null;

        // AO-like: Crear sprite con el icono del item usando coordenadas de mundo
        // Centrar exactamente en el tile
        const sprite = this.scene.add
          .sprite(worldX, worldY, iconKey || '')
          .setOrigin(0.5, 0.5)
          .setDisplaySize(32, 32) // Los iconos son 32x32
          .setDepth(itemRectDepth)
          .setScrollFactor(1) // Moverse con el mundo
          .setAlpha(isInitialLoad ? 1 : 0); // Sin fade in en carga inicial
        
        // AO-like: Guardar coordenadas de mundo para actualización en cada frame
        sprite.setData('worldX', worldX);
        sprite.setData('worldY', worldY);

        // Si no hay icono, usar un rectángulo placeholder
        let placeholder: Phaser.GameObjects.Rectangle | null = null;
        if (!iconKey) {
          sprite.setVisible(false);
          placeholder = this.scene.add
            .rectangle(worldX, worldY, 10, 10, 0xffff00, 0.9)
            .setOrigin(0.5)
            .setDepth(itemRectDepth)
            .setScrollFactor(1)
            .setAlpha(isInitialLoad ? 1 : 0);
          sprite.setData('placeholder', placeholder);
        }

        // Obtener el nombre del item y color por rolledRarity de la instancia.
        const itemName = formatItemName(item.itemId, item.enchantId, item.rolledRarity, item.rolledAffixes);
        const rarity = item.rolledRarity as ItemRarity;
        const rarityColor = '#' + (ITEM_RARITY_COLORS[rarity] ?? 0xffffaa).toString(16).padStart(6, '0');

        const label = this.scene.addText(worldX, worldY + 20, itemName, {
            fontSize: '8px',
            color: rarityColor,
            backgroundColor: 'rgba(0,0,0,0.5)',
          })
          .setOrigin(0.5)
          .setDepth(itemLabelDepth)
          .setScrollFactor(1)
          .setAlpha(0)
          .setVisible(false);

        // Invisible zone covering the full tile for hover detection
        const hoverZone = this.scene.add.zone(worldX, worldY, TILE_SIZE, TILE_SIZE)
          .setOrigin(0.5)
          .setDepth(itemRectDepth + 0.02)
          .setScrollFactor(1)
          .setInteractive({ useHandCursor: false });
        sprite.setData('hoverZone', hoverZone);
        const hoverTarget = hoverZone;

        hoverTarget.on('pointerover', () => {
          // Estando muerto el hover de items no muestra nada (ni label ni cursor):
          // un muerto no levanta items.
          if (this.scene.isLocalPlayerDead()) return;

          label.setVisible(true);
          this.scene.tweens.add({ targets: label, alpha: 1, duration: 150 });

          // Hand cursor only if player is on the same tile.
          if (this.scene.localSessionId) {
            const localRender = this.scene.playerObjects.get(this.scene.localSessionId);
            if (localRender) {
              const playerTileX = Math.floor(localRender.currentWorldX / TILE_SIZE);
              const playerTileY = Math.floor(localRender.currentWorldY / TILE_SIZE);
              if (playerTileX === item.x && playerTileY === item.y) {
                this.scene.cursor.setGameCursor('click');
              }
            }
          }
        });

        hoverTarget.on('pointerout', () => {
          this.scene.cursor.setGameCursor('main');
          this.scene.tweens.add({
            targets: label,
            alpha: 0,
            duration: 150,
            onComplete: () => {
              label.setVisible(false);
            },
          });
        });

        hoverTarget.on('pointerup', () => {
          // No recoger si venimos arrastrando un hechizo (barra/spellbook): el
          // release del drag no debe disparar pickups del piso.
          if (this.scene.spellsPanel?.isDraggingSpell() || this.scene.spellQuickBar?.isDraggingSlot()) return;
          this.requestPickupGroundItem(item.id);
        });

        // Solo hacer fade in si NO es la carga inicial (solo el sprite, el label se muestra en hover)
        if (!isInitialLoad) {
          const targets: Phaser.GameObjects.GameObject[] = [sprite];
          const placeholder = sprite.getData('placeholder');
          if (placeholder) {
            targets.push(placeholder);
          }
          this.scene.tweens.add({
            targets,
            alpha: 1,
            duration: 200,
          });
        } else {
          // En carga inicial, el sprite ya está visible pero el label debe estar oculto
          sprite.setAlpha(1);
        }

        // Crear efecto visual para items de rareza SET (instancia).
        let effect: Phaser.GameObjects.Container | undefined;
        const groundRarity = item.rolledRarity as ItemRarity;
        if (groundRarity === ItemRarity.SET && ITEM_RARITY_COLORS[groundRarity]) {
          effect = this.createGroundItemEffect(worldX, worldY, ITEM_RARITY_COLORS[groundRarity], itemRectDepth);
          // Aplicar fade in al efecto si no es carga inicial
          if (!isInitialLoad) {
            effect.setAlpha(0);
            this.scene.tweens.add({
              targets: effect,
              alpha: 1,
              duration: 200,
            });
          }
        }

        // Crear efecto visual para encantamientos
        let enchantEffect: Phaser.GameObjects.Container | undefined;
        if (item.enchantId) {
          enchantEffect = this.createGroundEnchantmentEffect(worldX, worldY, itemRectDepth);
          // Aplicar fade in al efecto si no es carga inicial
          if (!isInitialLoad) {
            enchantEffect.setAlpha(0);
            this.scene.tweens.add({
              targets: enchantEffect,
              alpha: 1,
              duration: 200,
            });
          }
        }

        // Glow del ítem único: el brillo sale del BORDE del gráfico (silueta), colgado del sprite
        // vía preFX (WebGL). La luz real ya se registró arriba. En Canvas (sin preFX) cae al
        // glow-círculo detrás del sprite. El latido lo aplica el update por frame.
        let uniqueGlow: Phaser.FX.Glow | undefined;
        let uniqueEffect: Phaser.GameObjects.Container | undefined;
        if (isUnique) {
          uniqueGlow = this.attachUniqueGlow(sprite);
          if (!uniqueGlow) {
            // Fallback Canvas: glow-círculo (fade con el grupo del sprite).
            uniqueEffect = this.createUniqueGroundEffect(worldX, worldY, itemRectDepth);
            if (!isInitialLoad) {
              uniqueEffect.setAlpha(0);
              this.scene.tweens.add({
                targets: uniqueEffect,
                alpha: 1,
                duration: 200,
              });
            }
          }
        }

        this.scene.groundItemObjects.set(item.id, {
          sprite,
          label,
          effect,
          enchantEffect,
          uniqueEffect,
          uniqueGlow,
          itemId: item.itemId,
          quantity: item.quantity,
          id: item.id,
        });

        // FX propio del item en el piso (F5 `vfx.ground`) DESPUÉS del set: así el anchor
        // item_ground resuelve ya en el 1er frame (getItemGround lee groundItemObjects).
        if (itemTemplate?.vfx?.ground) {
          this.spawnGroundFx(itemTemplate.vfx.ground, item.id);
        }

      }
    });
  }

  /** Dispara un FX del catálogo sobre un ítem del piso (anchor item_ground, cleanup por owner). */
  private spawnGroundFx(fxId: string, groundItemId: string): void {
    this.scene.particleFx
      .ensureParticleManager()
      .spawn(
        fxId,
        { kind: 'item_ground', itemInstanceId: groundItemId },
        { ownerId: groundFxOwner(groundItemId), priority: 'low' }
      );
  }

  /**
   * Cuelga el glow de silueta al sprite del ítem único: el brillo NACE del borde del gráfico
   * (respeta el alpha del sprite), no un círculo detrás. Usa `preFX` (WebGL) con padding para que
   * el halo se extienda hacia afuera sin recortarse. Devuelve el FX para latir su `outerStrength`
   * por frame, o `undefined` si el renderer es Canvas (sin `preFX` → el caller cae al glow-círculo).
   */
  public attachUniqueGlow(
    sprite: Phaser.GameObjects.Sprite
  ): Phaser.FX.Glow | undefined {
    if (!sprite.preFX) return undefined; // Canvas: sin FX nativo
    sprite.preFX.setPadding(UNIQUE_GLOW_PADDING);
    return sprite.preFX.addGlow(
      UNIQUE_GLOW_COLOR,
      UNIQUE_GLOW_OUTER_MIN, // arranca en reposo; el heartbeat lo modula
      0, // innerStrength 0: sólo contorno externo, no lava el gráfico por dentro
      false, // knockout off: se ve el ítem, no sólo su halo
      UNIQUE_GLOW_QUALITY,
      UNIQUE_GLOW_DISTANCE
    );
  }

  /**
   * Late el glow de silueta por frame, en fase con la luz real (mismo reloj y período): el contorno
   * respira entre MIN (reposo) y MAX (golpe del latido). El sprite ya lleva la posición; el FX va
   * pegado a él, así que acá sólo se toca la intensidad.
   */
  private applyUniqueGlowHeartbeat(glow: Phaser.FX.Glow): void {
    const wave = heartbeatWave(this.scene.time.now / 1000 / HEARTBEAT_PERIOD_S);
    glow.outerStrength =
      UNIQUE_GLOW_OUTER_MIN + (UNIQUE_GLOW_OUTER_MAX - UNIQUE_GLOW_OUTER_MIN) * wave;
  }

  /**
   * Efecto sutil del ítem único en el piso: un glow aditivo cálido MUY suave (halo que resalta el
   * item sin tapar nada) + un par de glints diminutos que titilan. Estático al crearse; el latido
   * lo aplica `applyUniqueHeartbeat` por frame (sin tweens → cero fugas: al destruirse el container
   * deja de recibir updates), en fase con la luz real (mismo reloj/período). Va justo debajo del
   * sprite del item y sobre el glow de rareza SET.
   *
   * Cada hijo lleva `kind` ('glow' | 'glint') + `baseAlpha` para que el latido module distinto:
   * el glow respira apenas, los glints casi desaparecen en reposo y brillan en cada golpe.
   */
  public createUniqueGroundEffect(
    x: number,
    y: number,
    depth: number
  ): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);
    container.setDepth(depth - 0.008); // sobre el efecto SET (depth - 0.01), debajo del sprite
    container.setScrollFactor(1);

    // Halo suave: 2 capas de baja opacidad (mucho más tenue que los efectos SET/encantamiento).
    const layers = 2;
    const baseRadius = 16;
    for (let i = 0; i < layers; i++) {
      const baseAlpha = 0.085 * (1 - i * 0.35); // muy sutil
      const layerRadius = baseRadius * (0.85 + i * 0.3);
      const glow = this.scene.add.circle(0, 0, layerRadius, UNIQUE_GLOW_COLOR, baseAlpha);
      glow.setBlendMode(Phaser.BlendModes.ADD);
      glow.setData('kind', 'glow');
      glow.setData('baseAlpha', baseAlpha);
      container.add(glow);
    }

    // Glints: 3 chispas diminutas y cálidas repartidas sobre el item; titilan con el latido.
    const glintOffsets = [
      { x: 0, y: -9 },
      { x: 7, y: 4 },
      { x: -7, y: 3 },
    ];
    for (const off of glintOffsets) {
      const glint = this.scene.add.circle(off.x, off.y, 2.2, 0xfff6da, 0.6);
      glint.setBlendMode(Phaser.BlendModes.ADD);
      glint.setData('kind', 'glint');
      glint.setData('baseAlpha', 0.6);
      container.add(glint);
    }

    return container;
  }

  /**
   * Aplica el latido al efecto del único por frame, en fase con la luz real (mismo reloj y período).
   * El glow respira apenas; los glints se apagan en reposo y brillan en cada golpe (curva más dura).
   */
  private applyUniqueHeartbeat(container: Phaser.GameObjects.Container): void {
    const wave = heartbeatWave(this.scene.time.now / 1000 / HEARTBEAT_PERIOD_S);
    container.setScale(0.98 + 0.04 * wave); // respiración de escala muy leve
    for (const child of container.list) {
      const glow = child as Phaser.GameObjects.Arc;
      const base = glow.getData('baseAlpha') as number | undefined;
      if (base === undefined) continue;
      if (glow.getData('kind') === 'glint') {
        // Curva más dura (wave^2): casi invisibles en reposo, un guiño en el golpe.
        const g = wave * wave;
        glow.setAlpha(base * (0.15 + 0.85 * g));
        glow.setScale(0.7 + 0.5 * g);
      } else {
        glow.setAlpha(base * (0.7 + 0.4 * wave));
      }
    }
  }

  public createGroundItemEffect(
    x: number,
    y: number,
    color: number,
    depth: number
  ): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);
    container.setDepth(depth - 0.01); // Justo debajo del sprite del item
    container.setScrollFactor(1); // Moverse con el mundo

    // Crear múltiples capas de glow para efecto más rico
    const layers = 2;
    const baseRadius = 24; // Tamaño del glow (un poco más grande que el sprite de 32px)

    for (let i = 0; i < layers; i++) {
      const layerAlpha = 0.125 * (1 - i * 0.3); // Cada capa más transparente (50% menos opacidad)
      const layerRadius = baseRadius * (0.8 + i * 0.2); // Capas de diferentes tamaños
      
      // Crear círculo con glow (color blanco)
      const glow = this.scene.add.circle(0, 0, layerRadius, 0xffffff, layerAlpha);
      glow.setBlendMode(Phaser.BlendModes.ADD);
      container.add(glow);

      // Animación sutil de pulsación
      const minAlpha = layerAlpha * 0.4;
      const maxAlpha = layerAlpha;
      const delay = i * 150; // Cada capa con un pequeño delay
      
      // Animación de pulsación suave y discreta
      this.scene.tweens.add({
        targets: glow,
        alpha: { from: minAlpha, to: maxAlpha },
        duration: 2000 + i * 300, // Duración diferente para cada capa
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
        delay: delay,
      });
    }

    // Opcional: pequeño pulso de escala en el glow (muy sutil)
    this.scene.tweens.add({
      targets: container,
      scaleX: { from: 0.95, to: 1.05 },
      scaleY: { from: 0.95, to: 1.05 },
      duration: 2500,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });

    return container;
  }

  /**
   * Crea un efecto visual especial para items con encantamiento en el suelo
   * Este efecto debe ser más prominente que el de rareza y estar sobre él
   */
  public createGroundEnchantmentEffect(
    x: number,
    y: number,
    depth: number
  ): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);
    container.setDepth(depth - 0.005); // Sobre el efecto de rareza (depth - 0.01)
    container.setScrollFactor(1); // Moverse con el mundo

    // Color violeta claro para encantamientos
    const enchantColor = 0xaa88ff; // Violeta claro
    const enchantAlpha = 0.3; // 50% de la opacidad original (0.6 -> 0.3)

    // Capas de glow más prominentes
    const layers = 3;
    const baseRadius = 28; // Un poco más grande que el efecto de rareza

    for (let i = 0; i < layers; i++) {
      const layerAlpha = enchantAlpha * (1 - i * 0.25); // Cada capa más transparente
      const layerRadius = baseRadius * (0.85 + i * 0.15); // Capas de diferentes tamaños
      
      // Crear círculo con glow
      const glow = this.scene.add.circle(0, 0, layerRadius, enchantColor, layerAlpha);
      glow.setBlendMode(Phaser.BlendModes.ADD);
      container.add(glow);

      // Animación más intensa de pulsación
      const minAlpha = layerAlpha * 0.5;
      const maxAlpha = layerAlpha;
      const delay = i * 200;
      
      this.scene.tweens.add({
        targets: glow,
        alpha: { from: minAlpha, to: maxAlpha },
        scaleX: { from: 0.9, to: 1.15 },
        scaleY: { from: 0.9, to: 1.15 },
        duration: 1500 + i * 200,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
        delay: delay,
      });
    }

    // Anillo exterior pulsante
    const outerRing = this.scene.add.circle(0, 0, baseRadius * 1.1, enchantColor, 0);
    outerRing.setStrokeStyle(2, enchantColor, enchantAlpha);
    outerRing.setBlendMode(Phaser.BlendModes.ADD);
    container.add(outerRing);

    this.scene.tweens.add({
      targets: outerRing,
      scaleX: { from: 0.95, to: 1.2 },
      scaleY: { from: 0.95, to: 1.2 },
      alpha: { from: enchantAlpha * 0.4, to: enchantAlpha },
      duration: 1200,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });

    // Resplandor central pulsante
    const centerGlow = this.scene.add.circle(0, 0, baseRadius * 0.4, enchantColor, enchantAlpha * 0.7);
    centerGlow.setBlendMode(Phaser.BlendModes.ADD);
    container.add(centerGlow);

    this.scene.tweens.add({
      targets: centerGlow,
      scaleX: { from: 0.8, to: 1.4 },
      scaleY: { from: 0.8, to: 1.4 },
      alpha: { from: enchantAlpha * 0.5, to: enchantAlpha * 0.9 },
      duration: 1000,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });

    // Partículas/destellos en las 4 esquinas (más visibles)
    const sparkPositions = [
      { x: -baseRadius * 0.6, y: -baseRadius * 0.6 },
      { x: baseRadius * 0.6, y: -baseRadius * 0.6 },
      { x: baseRadius * 0.6, y: baseRadius * 0.6 },
      { x: -baseRadius * 0.6, y: baseRadius * 0.6 },
    ];

    sparkPositions.forEach((pos, index) => {
      const spark = this.scene.add.circle(pos.x, pos.y, 6, enchantColor, enchantAlpha * 0.9);
      spark.setBlendMode(Phaser.BlendModes.ADD);
      container.add(spark);

      this.scene.tweens.add({
        targets: spark,
        alpha: { from: enchantAlpha * 0.4, to: enchantAlpha },
        scaleX: { from: 0.5, to: 1.5 },
        scaleY: { from: 0.5, to: 1.5 },
        duration: 800 + index * 150,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
        delay: index * 200,
      });
    });

    return container;
  }

  public requestPickupGroundItem(groundItemId: string): void {
    if (!this.scene.colyseusClient) return;

    const msg = {
      type: ClientMessageType.INVENTORY_PICKUP,

      groundItemId,
    } as never;

    this.scene.colyseusClient.send(msg);
  }

  public updateGroundItemsPositions(): void {
    // Los items del piso son ESTÁTICOS: su posición (sprite + label + efectos) ya la fija
    // `applyGroundItemsSync` al crear/actualizar, y la cámara scrollea sobre coordenadas de MUNDO
    // (scrollFactor 1), así que no hace falta re-posicionarlos por frame. Antes se hacía
    // `setPosition` de sprite+label+efectos de CADA item en CADA frame → O(n) de transforms inútiles
    // (con la vitrina de debug = cientos de items ⇒ 50-60ms/frame sostenidos). Lo ÚNICO que necesita
    // update por frame es el LATIDO del glow de los ítems ÚNICOS: iteramos y sólo animamos esos
    // (el chequeo por item es barato: no toca el transform). El glow va pegado al sprite (no se
    // reposiciona); el fallback-círculo Canvas ya quedó en su lugar desde el sync.
    for (const obj of this.scene.groundItemObjects.values()) {
      if (obj.uniqueGlow) {
        this.applyUniqueGlowHeartbeat(obj.uniqueGlow);
      } else if (obj.uniqueEffect) {
        this.applyUniqueHeartbeat(obj.uniqueEffect);
      }
    }
  }

}
